import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Logo from '../../../Assets/logo.png';
import styles from './NavBar.module.css';
import LoginSignupContext from '../../../Reusables/VisualAndAccord/Context/LoginSignupContext';
import LogIn from '../../LogIn/LogIn';
import MainModal from '../../SignUp/MainScreen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const NavBar = () => {
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [solutionsTimeout, setSolutionsTimeout] = useState(null);
  const [resourcesTimeout, setResourcesTimeout] = useState(null);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const user = useSelector((state) => state.userReducer);
  const storedToken = localStorage.getItem('jwt_token');
  
  const getCookie = (name) => {
    const doc = typeof globalThis !== 'undefined' ? globalThis.document : null;

    if (!doc) return null;
    const value = `; ${doc.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const token = storedToken || user?.token || getCookie('jwt_token');
  const isAuthenticated = !!token;


  const { loginModal, setLoginModal, signupModal, setSignupModal, setIdx } =
    useContext(LoginSignupContext);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        solutionsOpen &&
        !event.target.closest(`.${styles.solutionsContainer}`)
      ) {
        setSolutionsOpen(false);
      }
      if (
        resourcesOpen &&
        !event.target.closest(`.${styles.resourcesContainer}`)
      ) {
        setResourcesOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [solutionsOpen, resourcesOpen]);

  const handleSolutionsEnter = () => {
    if (solutionsTimeout) clearTimeout(solutionsTimeout);
    setSolutionsOpen(true);
  };

  const handleSolutionsLeave = () => {
    const timer = setTimeout(() => {
      setSolutionsOpen(false);
    }, 150);
    setSolutionsTimeout(timer);
  };

  const handleSolutionsClick = () => {
    setSolutionsOpen(!solutionsOpen);
  };

  const handleResourcesEnter = () => {
    if (resourcesTimeout) clearTimeout(resourcesTimeout);
    setResourcesOpen(true);
  };

  const handleResourcesLeave = () => {
    const timer = setTimeout(() => {
      setResourcesOpen(false);
    }, 150);
    setResourcesTimeout(timer);
  };

  const handleResourcesClick = () => {
    setResourcesOpen(!resourcesOpen);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isLinkActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderSolutionsDropdown = () => {
    return (
      <div className={`${styles.solutionsDropdown} ${solutionsOpen ? styles.dropdownVisible : ''}`}>
        <Link
          to="/individual-users"
          className={`${styles.dropdownItem} ${
            isLinkActive('/individual-users') ? styles.active : ''
          }`}
          onClick={() => { setSolutionsOpen(false); }}
        >
          Individual Users
        </Link>
        <Link
          to="/financial-advisors"
          className={`${styles.dropdownItem} ${
            isLinkActive('/financial-advisors') ? styles.active : ''
          }`}
          onClick={() => { setSolutionsOpen(false); }}
        >
          Financial Advisors
        </Link>
      </div>
    );
  };

  const renderResourcesDropdown = () => {
    return (
      <div className={`${styles.solutionsDropdown} ${resourcesOpen ? styles.dropdownVisible : ''}`}>
         <Link
          to="/find-old-401k"
          className={`${styles.dropdownItem} ${
            isLinkActive('/find-old-401k') ? styles.active : ''
          }`}
          onClick={() => { setResourcesOpen(false); }}
        >
          Find Old 401k
        </Link>
        <Link
          to="/retirement-calculator"
          className={`${styles.dropdownItem} ${
            isLinkActive('/retirement-calculator') ? styles.active : ''
          }`}
          onClick={() => { setResourcesOpen(false); }}
        >
          Retirement Calculator
        </Link>
        <Link
          to="/unclaimed-money"
          className={`${styles.dropdownItem} ${
            isLinkActive('/unclaimed-money') ? styles.active : ''
          }`}
          onClick={() => { setResourcesOpen(false); }}
        >
          Unclaimed Money
        </Link>
        <Link
          to="/where-is-my-refund"
          className={`${styles.dropdownItem} ${
            isLinkActive('/where-is-my-refund') ? styles.active : ''
          }`}
          onClick={() => { setResourcesOpen(false); }}
        >
          Where Is My Refund?
        </Link>
       
      </div>
    );
  };

  return (
    <nav className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <Link to="/" className={styles.logoContainer}>
          <img src={Logo} alt="Plootus" className={styles.logo} />
        </Link>

        {/* Desktop Navigation */}
        <div className={styles.navLinks}>
          <Link
            to="/"
            className={`${styles.navLink} ${
              isLinkActive('/') ? styles.active : ''
            }`}
          >
            Overview
          </Link>

          <div 
            className={styles.solutionsContainer}
            onMouseEnter={handleSolutionsEnter}
            onMouseLeave={handleSolutionsLeave}
          >
            <button
              className={`${styles.navLink} ${styles.solutionsButton} ${
                isLinkActive('/individual-users') ||
                isLinkActive('/financial-advisors')
                  ? styles.active
                  : ''
              }`}
              onClick={handleSolutionsClick}
            >
              Solutions
              <KeyboardArrowDownIcon
                className={`${styles.dropdownIcon} ${
                  solutionsOpen ? styles.dropdownIconOpen : ''
                }`}
              />
            </button>
            {renderSolutionsDropdown()}
          </div>

          <Link
            to="/partners"
            className={`${styles.navLink} ${
              isLinkActive('/partners') ? styles.active : ''
            }`}
          >
            Partners
          </Link>

          <Link
            to="/newsletter"
            className={`${styles.navLink} ${
              isLinkActive('/newsletter') ? styles.active : ''
            }`}
          >
            Newsletter
          </Link>

          <Link
            to="/finance-101"
            className={`${styles.navLink} ${
              isLinkActive('/finance-101') ? styles.active : ''
            }`}
          >
            Finance 101
          </Link>

          <Link
            to="/press"
            className={`${styles.navLink} ${
              isLinkActive('/press') ? styles.active : ''
            }`}
          >
            Press
          </Link>

          <div 
            className={styles.resourcesContainer}
            onMouseEnter={handleResourcesEnter}
            onMouseLeave={handleResourcesLeave}
          >
            <button
              className={`${styles.navLink} ${styles.solutionsButton} ${
                isLinkActive('/where-is-my-refund') ||
                isLinkActive('/retirement-calculator') ||
                isLinkActive('/unclaimed-money') ||
                isLinkActive('/find-old-401k')
                  ? styles.active
                  : ''
              }`}
              onClick={handleResourcesClick}
            >
              Resources
              <KeyboardArrowDownIcon
                className={`${styles.dropdownIcon} ${
                  resourcesOpen ? styles.dropdownIconOpen : ''
                }`}
              />
            </button>
            {renderResourcesDropdown()}
          </div>

          <Link
            to="/about"
            className={`${styles.navLink} ${
              isLinkActive('/about') ? styles.active : ''
            }`}
          >
            About Us
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={styles.mobileMenuButton}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        {/* Desktop Auth Buttons */}
        {!isAuthenticated ? (
          <div className={styles.authButtons}>
            <div className={styles.loginWrapper}>
              <LogIn
                modalIsOpen={loginModal}
                openModal={() => setLoginModal(true)}
                closeModal={() => setLoginModal(false)}
                signupopenModal={() => setSignupModal(true)}
                fromFees={false}
              />
            </div>

            <div className={styles.signupWrapper}>
              <MainModal
                modalIsOpen={signupModal}
                openModal={() => setSignupModal(true)}
                closeModal={() => setSignupModal(false)}
                loginopenModal={() => setLoginModal(true)}
                fromFees={false}
              />
            </div>
          </div>
        ) : (
          <div className={styles.authButtons}>
            <Link
              to={user?.userData?.userType === 'advisor' ? '/auth/adash' : '/auth/dashboard'}
              className={styles.dashboardButton}
            >
              My Dashboard
            </Link>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      <div
        className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}
      >
        <div className={styles.mobileNavLinks}>
          {isAuthenticated && (
            <Link
              to={user?.userData?.userType === 'advisor' ? '/auth/adash' : '/auth/dashboard'}
              className={`${styles.mobileNavLink} ${
                isLinkActive('/auth/dashboard') || isLinkActive('/auth/adash') ? styles.active : ''
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </Link>
          )}

          <Link
            to="/"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Overview
          </Link>

          <div className={styles.mobileSolutionsContainer}>
            <button
              className={`${styles.mobileSolutionsButton} ${
                isLinkActive('/individual-users') ||
                isLinkActive('/financial-advisors')
                  ? styles.active
                  : ''
              }`}
              onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
            >
              Solutions
              <KeyboardArrowDownIcon
                className={`${styles.dropdownIcon} ${
                  mobileSolutionsOpen ? styles.dropdownIconOpen : ''
                }`}
              />
            </button>
            <div className={`${styles.mobileSubLinks} ${mobileSolutionsOpen ? styles.mobileSubLinksOpen : ''}`}>
                <Link
                  to="/individual-users"
                  className={isLinkActive('/individual-users') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Individual Users
                </Link>
                <Link
                  to="/financial-advisors"
                  className={isLinkActive('/financial-advisors') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Financial Advisors
                </Link>
              </div>
          </div>

          <Link
            to="/partners"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/partners') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Partners
          </Link>

          <Link
            to="/newsletter"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/newsletter') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Newsletter
          </Link>

          <Link
            to="/finance-101"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/finance-101') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Finance 101
          </Link>

          <Link
            to="/press"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/press') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Press
          </Link>

          <div className={styles.mobileSolutionsContainer}>
            <button
              className={`${styles.mobileSolutionsButton} ${
                isLinkActive('/where-is-my-refund') ||
                isLinkActive('/retirement-calculator') ||
                isLinkActive('/unclaimed-money') ||
                isLinkActive('/find-old-401k')
                  ? styles.active
                  : ''
              }`}
              onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
            >
              Resources
              <KeyboardArrowDownIcon
                className={`${styles.dropdownIcon} ${
                  mobileResourcesOpen ? styles.dropdownIconOpen : ''
                }`}
              />
            </button>
            <div className={`${styles.mobileSubLinks} ${mobileResourcesOpen ? styles.mobileSubLinksOpen : ''}`}>
                <Link
                  to="/retirement-calculator"
                  className={isLinkActive('/retirement-calculator') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Retirement Calculator
                </Link>
                <Link
                  to="/unclaimed-money"
                  className={isLinkActive('/unclaimed-money') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Unclaimed Money
                </Link>
                <Link
                  to="/where-is-my-refund"
                  className={isLinkActive('/where-is-my-refund') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Where Is My Refund?
                </Link>
                <Link
                  to="/find-old-401k"
                  className={isLinkActive('/find-old-401k') ? styles.active : ''}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Find Old 401k
                </Link>
              </div>
          </div>

          <Link
            to="/about"
            className={`${styles.mobileNavLink} ${
              isLinkActive('/about') ? styles.active : ''
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About Us
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
